// utils/interceptors.ts
import { HIDE_ALL_MODALS, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { saveLog, store } from '@reduxLocal/persistState';
import { Platform } from 'react-native';

/**
 * Apply interceptors to an Axios instance.
 *
 * @param {AxiosInstance} axiosInstance - Axios instance to wrap.
 * @param {Function} dispatch - Redux dispatch function.
 * @returns {AxiosInstance} - The Axios instance with applied interceptors.
 */

function dispatch(action) {
	store.dispatch(action);
}

const applyInterceptor = (axiosInstance: any) => {
	// Request interceptor
	axiosInstance.interceptors.request.use(
		async (config) => {
			try {
				// Log the request
				const log = {
					type: 'API_REQUEST',
					method: config.method,
					url: config.url,
					timestamp: new Date().toISOString(),
				};
				if (Platform.OS === 'web') {
					await saveLog(log);
				}
				return config;
			} catch (error) {
				console.warn('Logging failed:', error);
				return config;
			}
		},
		(error) => {
			console.error('Request Error:', error);
			return Promise.reject(error);
		}
	);

	// Response interceptor
	axiosInstance.interceptors.response.use(
		(response) => response,
		(error) => {
			dispatch({ type: HIDE_ALL_MODALS });

			if (error?.response) {
				dispatch({
					type: SHOW_AUTH_MODAL,
					data: {
						content: `Error: ${error.response.data?.error || 'Unknown error'}`,
						type: 'error',
					},
				});
			} else {
				dispatch({
					type: SHOW_AUTH_MODAL,
					data: {
						content: `An unexpected error occurred. ${error.message}`,
						type: 'error',
					},
				});
			}

			return Promise.reject(error);
		}
	);

	return axiosInstance; // Return the Axios instance for chaining.
};

export default applyInterceptor;
